import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, NgZone } from '@angular/core';
import { ExportService } from '../../services/export.service';
import type { MapsExportFormat } from '../../models/maps.types';

/**
 * Conservative ceiling when the parent hasn't given us a live `cap` yet (e.g.
 * the session validate request hasn't returned). Matches the FRD's original
 * 30-candidate hard cap so we never default high.
 */
const DEFAULT_EXPORT_CAP = 30;

@Component({
    selector: 'maps-export-button',
    standalone: false,
    templateUrl: './export-button.component.html',
    styleUrl: './export-button.component.scss',
})
export class ExportButtonComponent {
    @Input({ required: true }) searchId!: string | null;
    @Input({ required: true }) candidateIds: string[] = [];
    /**
     * Per the 6-8 client meeting: the export ceiling tracks the admin-tunable
     * `defaultResultCount` (Configuration tab → Result Set) — one knob, no
     * separate hardcoded EXPORT_CAP. Parent reads it from mapsSessionValidate
     * and passes it down. Falls back to DEFAULT_EXPORT_CAP before the first
     * session response so the UI never sits in an unbounded state.
     */
    @Input() cap: number = DEFAULT_EXPORT_CAP;

    format: MapsExportFormat = 'xlsx';
    includeSummaries = true;
    isExporting = false;
    errorMessage: string | null = null;
    isOpen = false;

    constructor(
        private readonly exportService: ExportService,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef,
        private readonly host: ElementRef<HTMLElement>,
    ) {}

    ToggleMenu(): void {
        if (this.count === 0) return;
        this.isOpen = !this.isOpen;
    }

    @HostListener('document:click', ['$event'])
    OnDocumentClick(event: MouseEvent): void {
        if (!this.host.nativeElement.contains(event.target as Node)) {
            this.isOpen = false;
        }
    }

    @HostListener('document:keydown.escape')
    OnEscape(): void {
        this.isOpen = false;
    }

    get count(): number {
        return this.candidateIds.length;
    }
    get overCap(): boolean {
        return this.count > this.cap;
    }
    get disabled(): boolean {
        return !this.searchId || this.count === 0 || this.overCap || this.isExporting;
    }

    async Export(): Promise<void> {
        if (this.disabled || !this.searchId) return;
        this.isExporting = true;
        this.errorMessage = null;
        try {
            const result = await this.exportService.Create({
                searchId: this.searchId,
                candidateIds: this.candidateIds.slice(0, this.cap),
                format: this.format,
                includeSummaries: this.includeSummaries,
            });
            // Same-tab hidden-anchor click: the server bakes
            // `Content-Disposition: attachment` into the blob at upload, so
            // this navigation downloads WITHOUT unloading the SPA. Deliberately
            // no target="_blank" — by the time the awaited mutation resolves
            // the click's user-activation token has expired and popup blockers
            // silently drop new-tab navigations (8-11 deployed-run finding:
            // export "succeeded" with no file and no console error).
            const anchor = document.createElement('a');
            anchor.href = result.downloadUrl;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
        } catch (err) {
            this.zone.run(() => {
                this.errorMessage = (err as Error)?.message ?? 'Export failed';
                this.cdr.detectChanges();
            });
        } finally {
            this.zone.run(() => {
                this.isExporting = false;
                if (!this.errorMessage) this.isOpen = false;
                this.cdr.detectChanges();
            });
        }
    }
}
